import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { JerseyNumber } from "../../components/rink/JerseyNumber";
import { Button } from "../../components/ui/Button";
import { CopyField } from "../../components/ui/CopyField";
import { Modal } from "../../components/ui/Modal";
import { useI18n } from "../../i18n/I18nProvider";

export interface LinkModalProps {
  personName: string;
  personNumber?: number | null;
  url: string;
  /** True right after a new link replaced an old one: the old link no longer works. */
  regenerated?: boolean;
  onClose: () => void;
}

/**
 * Shows one player's link: the link in a CopyField, Share where the platform has it, and a QR code on a
 * white board with the player's name and number. The link can be shown again later from the roster.
 */
export function LinkModal({
  personName,
  personNumber = null,
  url,
  regenerated = false,
  onClose,
}: LinkModalProps) {
  const { t } = useI18n();
  const [qr, setQr] = useState<string | null>(null);
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(url, { width: 240, margin: 1, color: { dark: "#000000", light: "#ffffff" } })
      .then((dataUrl) => {
        if (!cancelled) setQr(dataUrl);
      })
      .catch(() => {
        if (!cancelled) setQr(null);
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  const share = async () => {
    try {
      await navigator.share({ title: personName, url });
    } catch {
      // The user dismissed the share sheet, or the platform refused: nothing to report.
    }
  };

  return (
    <Modal title={t("link.title")} onClose={onClose}>
      <div className="space-y-4">
        <p className="text-operate text-ink-2">{t("link.intro", { name: personName })}</p>
        {regenerated ? (
          <p
            role="note"
            className="rounded-control bg-red-wash px-3 py-2 text-note font-medium text-red"
          >
            {t("link.regenerated")}
          </p>
        ) : null}
        <CopyField value={url} />
        {canShare ? <Button onClick={() => void share()}>{t("link.share")}</Button> : null}
        {qr ? (
          <div className="flex flex-col items-center gap-2 rounded-panel bg-white p-5 text-center shadow-board">
            {personNumber === null ? null : <JerseyNumber value={personNumber} size="md" />}
            <p className="max-w-full truncate text-note font-semibold text-ink-2">{personName}</p>
            <img
              src={qr}
              alt={t("link.qrLabel")}
              width={240}
              height={240}
              className="size-60 max-w-full"
            />
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
